<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { ModalWrapper } from '@/shared/ui/modals'
import { useEventDeleteMutation } from '@/enitites/event/api/queries'
import { useRoute, useRouter } from 'vue-router/composables'

const currentEventStep = inject<number>('currentEventStep', 0)
const totalEventSteps = inject<number>('totalEventSteps', 1)
const isCreationFlow = inject<boolean>('isCreationFlow', false)

const props = defineProps<{
  isLoading: boolean
  disabled: boolean
  error?: string
  nextButtonText?: string
  hideCancelButton?: boolean
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const { mutate: deleteEvent, isPending } = useEventDeleteMutation()
const route = useRoute()
const router = useRouter()

const isShowCancelModal = ref(false)

async function removeEvent() {
  if (isPending.value || !route.params.id) return

  deleteEvent(Number(route.params.id), {
    onSuccess: () => {
      router.push({ name: 'EventsGrid' })
    },
  })
}

const computedNextButtonText = computed(() => {
  if (props.nextButtonText) {
    return props.nextButtonText
  }
  return isCreationFlow && currentEventStep + 1 !== totalEventSteps ? 'next' : 'save'
})
</script>

<template>
  <div class="navigation">
    <div
      v-if="totalEventSteps > 1"
      class="navigation__wrap-dots mt-6"
    >
      <span
        v-for="i in totalEventSteps"
        :key="i"
        :class="i === currentEventStep + 1 ? 'navigation__dot--active' : ''"
        class="navigation__dot"
      />
    </div>
    <span
      v-if="error"
      class="navigation__text-error"
    >
      {{ error }}
    </span>
    <div class="navigation__wrap-btn">
      <div class="d-flex flex-column flex-sm-row-reverse">
        <BKButton
          :loading="isLoading"
          :disabled="disabled"
          class="navigation__btn"
          color="var(--color-blue-800)"
          height="52"
          width="auto"
          white-text
          @click="emit('next')"
        >
          {{ computedNextButtonText }}
        </BKButton>
        <BKButton
          :disabled="isCreationFlow ? !currentEventStep || isLoading : isLoading"
          class="navigation__btn"
          color="var(--color-blue-800)"
          height="52"
          width="auto"
          outlined
          @click="emit('back')"
        >
          {{ isCreationFlow ? 'back' : 'cancel' }}
        </BKButton>
      </div>
      <v-btn
        v-if="isCreationFlow && !hideCancelButton"
        block
        class="navigation__btn-cancel"
        color="var(--color-blue-800)"
        height="52"
        max-width="194"
        text
        @click="isShowCancelModal = true"
      >
        cancel
      </v-btn>
    </div>

    <ModalWrapper
      :is-show="isShowCancelModal"
      @update:isShow="isShowCancelModal = $event"
    >
      <v-card tile>
        <div class="col-8 py-10 mx-auto">
          <v-container>
            <v-row>
              <v-card-title class="cancel__title"> Cancel Event</v-card-title>
            </v-row>
            <v-card-text class="pa-1">
              You can delete the event or save it as a draft and work on it later.</v-card-text
            >
          </v-container>

          <v-card-actions
            :class="{ 'flex-column': $vuetify.breakpoint.xsOnly }"
            class="d-flex"
          >
            <v-btn
              class="px-10 mx-3 my-2"
              color="#f21849"
              height="48"
              outlined
              tile
              @click="removeEvent"
            >
              Delete event
            </v-btn>
            <v-btn
              class="px-10 mx-3 my-2"
              color="#000000"
              height="48"
              outlined
              tile
              @click="emit('cancel')"
            >
              Save as draft
            </v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </ModalWrapper>
  </div>
</template>

<style scoped lang="scss">
.navigation {
  max-width: 400px;
  margin: 0 auto;

  &__wrap-dots {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: 15px;
  }

  &__dot {
    width: 8px;
    height: 8px;
    margin: 5px;
    background-color: #ddd;
    border-radius: 50%;
  }

  &__dot--active {
    background-color: #041e42;
  }

  &__wrap-btn {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
  }

  &__btn {
    flex-grow: 1;
    margin: 4px;
    font-weight: bold;
    border: 2px solid;

    &-cancel {
      font-weight: bold;
    }
  }

  &__text-error {
    color: red;
  }
}
</style>
